import { computed, inject } from '@angular/core';
import { Message } from '../interfaces/chatbot.interface';
import { StorageService } from '../../../shared/services/storage.service';
import { ChatbotService } from '../services/chatbot.service';
import { ConfigStore } from '../../../shared/store/config.store';
import { SessionStore } from '../../../shared/store/session.store';
import { SoundService } from '../../../shared/services/sound.service';
import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { getEasterEggMessage, hasEasterEggMessageTrigger } from '../../../shared/utils/easteregg.utils';

type ChatbotState = {
    chatHistory: Message[];
    showChatbot: boolean;
};

const initialState: ChatbotState = {
    chatHistory: [],
    showChatbot: false,
};

export const ChatbotStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withProps(() => ({
        HISTORY_KEY: 'CHAT_HISTORY',
    })),
    withComputed((store) => ({
        chatbotWriting: computed(() => {
            const history = store.chatHistory();
            return history.length > 0 && history.at(-1)?.role === 'user';
        }),
        lastMessageIsFromChatbot: computed(() => {
            const history = store.chatHistory();
            return history.length > 0 && history.at(-1)?.role === 'chatbot';
        }),
    })),
    withMethods(
        (
            store,
            chatbotService = inject(ChatbotService),
            soundService = inject(SoundService),
            configStore = inject(ConfigStore),
            sessionStore = inject(SessionStore),
            storageService = inject(StorageService),
        ) => ({
            sendMessage(text: string): void {
                if (sessionStore.sessionId() == null || configStore.clientId() == null) {
                    return;
                }
                this._platSendMessageSound();
                // Ajout du message à l'historique
                this._addMessageToHistory({ text, role: 'user', date: new Date() });
                // Check si le message est un easteregg
                if (hasEasterEggMessageTrigger(text)) {
                    this._playNotificationSound();
                    const response: Message = {
                        text: getEasterEggMessage(text),
                        role: 'chatbot',
                        date: new Date(),
                        type: 'SUCCESS',
                    };
                    this._addMessageToHistory(response);
                    return;
                }
                // Mode preview : retourne un message mocké
                if (configStore.isPreviewMode()) {
                    setTimeout(() => {
                        this._playNotificationSound();
                        this._addMessageToHistory({
                            text: 'Ceci est une réponse de prévisualisation. Sur votre site web, le chatbot répondra à toutes vos questions.',
                            role: 'chatbot',
                            date: new Date(),
                            type: 'SUCCESS',
                        });
                    }, 500);
                    return;
                }
                // Envoie du message et réception de la réponse
                chatbotService.sendMessage(text, sessionStore.sessionId(), configStore.clientId()).subscribe({
                    next: (message: Message) => {
                        if (message.text) {
                            try {
                                const t = JSON.parse(message.text);
                                if (503 === t.error?.code && t.error.status === 'UNAVAILABLE') {
                                    message.type = 'ERROR';
                                    message.text = 'Le service est temporairement indisponible. Veuillez réessayer.';
                                }
                            } catch {
                                message.type = 'SUCCESS';
                            }
                        }

                        // Ajout de la réponse à l'historique
                        if (message.text?.length > 0) {
                            this._addMessageToHistory(message);
                            this._playNotificationSound();
                        }
                    },
                    error: (error) => {
                        console.error("Erreur lors de l'envoi du message:", error);
                        // Ajout d'un message d'erreur à l'historique
                        this._addMessageToHistory({
                            text: 'Une erreur est survenue, veuillez réessayer.',
                            role: 'chatbot',
                            date: new Date(),
                            type: 'ERROR',
                        });
                    },
                });
            },

            toggleChatbot(show?: boolean): void {
                patchState(store, { showChatbot: show ?? !store.showChatbot() });
                // Send event to parent iframe
                window.parent.postMessage(
                    {
                        type: 'chatbot-state',
                        isOpen: store.showChatbot(),
                    },
                    '*',
                );
            },

            loadHistory(): void {
                const history: Message[] = storageService.get<Message[]>(store.HISTORY_KEY) || [];
                if (history?.length === 0) {
                    this._initFirstMessage();
                } else {
                    patchState(store, { chatHistory: history });
                }
            },

            removeHistory(): void {
                patchState(store, { chatHistory: [] });
                storageService.remove(store.HISTORY_KEY);
            },

            reset(): void {
                patchState(store, initialState);
                storageService.remove(store.HISTORY_KEY);
            },

            _addMessageToHistory(message: Message): void {
                patchState(store, { chatHistory: [...store.chatHistory(), message] });
                storageService.set(store.HISTORY_KEY, store.chatHistory());
            },

            markMessageAsDisplayed(index: number): void {
                const history = [...store.chatHistory()];
                if (history[index]) {
                    history[index] = { ...history[index], hasBeenDisplayed: true };
                    patchState(store, { chatHistory: history });
                    storageService.set(store.HISTORY_KEY, history);
                }
            },

            _initFirstMessage(): void {
                if (sessionStore.sessionId() == null || configStore.clientId() == null) {
                    return;
                }

                // Mode preview : message d'accueil mocké
                if (configStore.isPreviewMode()) {
                    this._addMessageToHistory({
                        text: configStore.config()?.welcomeMessage || 'Bonjour, comment puis-je vous aider ?',
                        role: 'chatbot',
                        date: new Date(),
                        type: 'SUCCESS',
                    });
                    this._playNotificationSound();
                    return;
                }

                chatbotService.sendMessage('init', sessionStore.sessionId(), configStore.clientId()).subscribe((message: Message) => {
                    this._addMessageToHistory(message);
                    this._playNotificationSound();
                });
            },

            _playNotificationSound(): void {
                if (configStore.config().enableReceiveMessageSound) {
                    soundService.playNotificationSound();
                }
            },

            _platSendMessageSound(): void {
                if (configStore.config().enableSendMessageSound) {
                    soundService.playSendMessageSound();
                }
            },
        }),
    ),
    withHooks((store, chatbotService = inject(ChatbotService)) => ({
        onInit() {
            // Ping toutes les 4 pour garder le model éveillé quand le chat est ouvert
            setInterval(
                () => {
                    chatbotService.ping().subscribe();
                },
                4 * 60 * 1000,
            );
        },
    })),
);
